import React, { useState, useCallback } from 'react';
import axios from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  Download, 
  RefreshCw, 
  Users, 
  TrendingUp, 
  Zap, 
  Target,
  Award,
  BarChart3,
  FileText,
  X,
  Copy,
  FileSpreadsheet,
  Eye,
  Building2,
  Mail,
  Activity,
  Gift,
  Megaphone,
  CheckCircle,
  UserCheck,
  Heart
} from 'lucide-react';

const API_BASE = '';

// Stat card component for the preview modal
const StatCard = ({ title, value, icon: Icon, color = '#df1283', subValue }) => (
  <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-[#FDF2F8]/60 mb-1">{title}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
        {subValue && <p className="text-xs text-[#0FFFE2] mt-1">{subValue}</p>}
      </div>
      {Icon && <Icon className="w-5 h-5" style={{ color }} />}
    </div>
  </div>
);

// Section header component
const SectionHeader = ({ title, icon: Icon, color = '#df1283' }) => (
  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#4A0A66]">
    {Icon && <Icon className="w-4 h-4" style={{ color }} />}
    <h3 className="text-sm font-semibold text-white">{title}</h3>
  </div>
);

const InvestorToolsTab = () => {
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);

  // Fetch investor analytics data
  const fetchInvestorAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/admin/export/investor-analytics`, {
        headers: { 'X-Admin-Token': localStorage.getItem('adminToken') }
      });
      setAnalyticsData(response.data);
      setLastFetched(new Date());
      setShowPreviewModal(true);
      toast.success('Investor analytics loaded successfully');
    } catch (err) {
      console.error('Failed to fetch investor analytics:', err);
      toast.error('Failed to load investor analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  // Download as JSON
  const downloadJSON = useCallback(() => {
    if (!analyticsData) return;
    
    const dataStr = JSON.stringify(analyticsData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `investor-analytics-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('JSON downloaded');
  }, [analyticsData]);

  // Download as CSV
  const downloadCSV = useCallback(() => {
    if (!analyticsData) return;
    
    // Flatten the nested data for CSV format
    const rows = [
      ['Metric Category', 'Metric Name', 'Value'],
      // User Metrics
      ['User Metrics', 'Total Users', analyticsData.user_metrics?.total_users || 0],
      ['User Metrics', 'Google OAuth Users', analyticsData.user_metrics?.by_auth_type?.google_oauth || 0],
      ['User Metrics', 'Email/Password Users', analyticsData.user_metrics?.by_auth_type?.email_password || 0],
      ['User Metrics', 'Admin Created Users', analyticsData.user_metrics?.by_auth_type?.admin_created || 0],
      ['User Metrics', 'Onboarding Completed', analyticsData.user_metrics?.onboarding?.completed || 0],
      ['User Metrics', 'Onboarding Completion Rate', analyticsData.user_metrics?.onboarding?.completion_rate || '0%'],
      // Signup Trends
      ['Signup Trends', 'Last 30 Days', analyticsData.signup_trends?.last_30_days || 0],
      ['Signup Trends', 'Last 90 Days', analyticsData.signup_trends?.last_90_days || 0],
      // Engagement
      ['Engagement', 'Total Journal Entries', analyticsData.engagement?.total_journal_entries || 0],
      ['Engagement', 'Users with Entries', analyticsData.engagement?.users_with_entries || 0],
      ['Engagement', 'Entries Last 30 Days', analyticsData.engagement?.entries_last_30_days || 0],
      ['Engagement', 'Avg Entries per User', analyticsData.engagement?.avg_entries_per_user || 0],
      ['Engagement', 'Activation Rate', analyticsData.engagement?.activation_rate || '0%'],
      // Feature Usage
      ['Feature Usage', 'Voice Typing Users', analyticsData.feature_usage?.voice_typing?.users || 0],
      ['Feature Usage', 'Total Transcriptions', analyticsData.feature_usage?.voice_typing?.total_transcriptions || 0],
      ['Feature Usage', 'AI Coach Users', analyticsData.feature_usage?.ai_coach?.users || 0],
      ['Feature Usage', 'Total AI Prompts', analyticsData.feature_usage?.ai_coach?.total_prompts || 0],
      // Gamification
      ['Gamification', 'Users with Active Streak', analyticsData.gamification?.users_with_active_streak || 0],
      ['Gamification', 'Highest Streak', analyticsData.gamification?.highest_streak || 0],
      ['Gamification', 'Total Badges Earned', analyticsData.gamification?.total_badges_earned || 0],
      // Active Users
      ['Active Users', 'Active Last 7 Days', analyticsData.active_users?.active_last_7_days || 0],
      ['Active Users', 'Active Last 30 Days', analyticsData.active_users?.active_last_30_days || 0],
      ['Active Users', 'Activity Rate (7d)', analyticsData.active_users?.activity_rate_7d || '0%'],
      ['Active Users', 'Activity Rate (30d)', analyticsData.active_users?.activity_rate_30d || '0%'],
      ['Active Users', 'Reactivated Users', analyticsData.active_users?.reactivated_users || 0],
      ['Active Users', 'Recovered from Deletion', analyticsData.active_users?.recovered_from_deletion || 0],
      // Banner Marketing
      ['Banner Marketing', 'Total Banners Created', analyticsData.banner_marketing?.total_banners_created || 0],
      ['Banner Marketing', 'Active Banners', analyticsData.banner_marketing?.active_banners || 0],
      ['Banner Marketing', 'Total Impressions', analyticsData.banner_marketing?.total_impressions || 0],
      ['Banner Marketing', 'Total Clicks', analyticsData.banner_marketing?.total_clicks || 0],
      ['Banner Marketing', 'Total Conversions', analyticsData.banner_marketing?.total_conversions || 0],
      ['Banner Marketing', 'Overall CTR', analyticsData.banner_marketing?.overall_ctr || '0%'],
      // B2B
      ['B2B', 'Total Clients', analyticsData.b2b?.total_clients || 0],
      ['B2B', 'Total B2B Users', analyticsData.b2b?.total_b2b_users || 0],
      // Marketing
      ['Marketing', 'Email Campaigns Sent', analyticsData.marketing?.email_campaigns_sent || 0],
      ['Marketing', 'Total Emails Sent', analyticsData.marketing?.total_emails_sent || 0],
      ['Marketing', 'Total Referrals', analyticsData.marketing?.referral_program?.total_referrals || 0],
      ['Marketing', 'Successful Referral Conversions', analyticsData.marketing?.referral_program?.successful_conversions || 0],
      // Data Integrity
      ['Data Integrity', 'Merged/Revoked Accounts', analyticsData.data_integrity?.merged_accounts || 0],
      ['Data Integrity', 'Active Accounts', analyticsData.data_integrity?.active_accounts || 0],
    ];
    
    // Add membership tier breakdown
    if (analyticsData.user_metrics?.by_membership_tier) {
      Object.entries(analyticsData.user_metrics.by_membership_tier).forEach(([tier, count]) => {
        rows.push(['Membership Tiers', tier, count]);
      });
    }
    
    // Add monthly signups
    if (analyticsData.signup_trends?.monthly_breakdown) {
      analyticsData.signup_trends.monthly_breakdown.forEach((m) => {
        rows.push(['Monthly Signups', m.month, m.signups]);
      });
    }
    
    const csvContent = rows.map(row => row.map(cell => 
      typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
    ).join(',')).join('\n');
    
    const dataBlob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `investor-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  }, [analyticsData]);

  // Copy to clipboard
  const copyToClipboard = useCallback(() => {
    if (!analyticsData) return;
    navigator.clipboard.writeText(JSON.stringify(analyticsData, null, 2));
    toast.success('Copied to clipboard');
  }, [analyticsData]);

  return (
    <div className="space-y-6" data-testid="investor-tools-tab-content">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#FF7901]" />
            Investor Tools
          </h2>
          <p className="text-sm text-[#FDF2F8]/60 mt-1">
            Export live analytics data for investor presentations and deck updates
          </p>
        </div>
        {lastFetched && (
          <div className="text-xs text-[#FDF2F8]/50" data-testid="investor-tools-last-fetched">
            Last fetched: {lastFetched.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Main Export Card */}
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white mb-2">
              Dynamic Investor Deck Data
            </h3>
            <p className="text-sm text-[#FDF2F8]/70 mb-4">
              Fetch the latest KPIs and metrics from your live database. Includes user growth, 
              engagement rates, feature adoption, banner ROI, reactivated users, and more.
            </p>
            <ul className="text-xs text-[#FDF2F8]/60 space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-[#0FFFE2]" /> Excludes merged/revoked accounts from all counts
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-[#0FFFE2]" /> Includes reactivated user metrics
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-[#0FFFE2]" /> Banner campaign analytics with ROI
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-[#0FFFE2]" /> Monthly signup trends and growth rates
              </li>
            </ul>
          </div>
          
          <div className="flex flex-col gap-3">
            <Button
              onClick={fetchInvestorAnalytics}
              disabled={loading}
              className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90 text-white px-6 py-3"
              data-testid="fetch-investor-analytics-btn"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Live Data
                </>
              )}
            </Button>
            
            {analyticsData && (
              <div className="flex gap-2">
                <Button
                  onClick={downloadJSON}
                  variant="outline"
                  size="sm"
                  className="border-[#0FFFE2] text-[#0FFFE2] hover:bg-[#0FFFE2]/20 flex-1"
                  data-testid="download-json-btn"
                >
                  <FileText className="w-3 h-3 mr-1" /> JSON
                </Button>
                <Button
                  onClick={downloadCSV}
                  variant="outline"
                  size="sm"
                  className="border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/20 flex-1"
                  data-testid="download-csv-btn"
                >
                  <FileSpreadsheet className="w-3 h-3 mr-1" /> CSV
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats Summary (if data is loaded) */}
      {analyticsData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="investor-tools-quick-stats">
          <StatCard 
            title="Total Users" 
            value={analyticsData.user_metrics?.total_users || 0}
            icon={Users}
            color="#df1283"
          />
          <StatCard 
            title="Active (30d)" 
            value={analyticsData.active_users?.active_last_30_days || 0}
            icon={Activity}
            color="#0FFFE2"
            subValue={analyticsData.active_users?.activity_rate_30d}
          />
          <StatCard 
            title="Reactivated" 
            value={analyticsData.active_users?.reactivated_users || 0}
            icon={Heart}
            color="#22C55E"
          />
          <StatCard 
            title="Journal Entries" 
            value={analyticsData.engagement?.total_journal_entries || 0}
            icon={FileText}
            color="#FF7901"
          />
        </div>
      )}

      {/* Links Section */}
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-xl p-6">
        <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#FF7901]" />
          Pitch Documents
        </h3>
        <div className="space-y-3">
          <a 
            href="/investor-deck-v9.0.html" 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="investor-deck-v9-link"
            className="flex items-center justify-between p-3 bg-[#1a0527] border border-[#4A0A66] rounded-lg hover:border-[#df1283] transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#df1283] to-[#FF7901] rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-medium group-hover:text-[#df1283] transition-colors">
                  Investor Deck v9.1
                </p>
                <p className="text-xs text-[#FDF2F8]/50">
                  Complete investor presentation with 170 pages (Updated Aug 10, 2026)
                </p>
              </div>
            </div>
            <span className="text-[#FDF2F8]/50 group-hover:text-[#df1283]">
              Open &rarr;
            </span>
          </a>
          
          <a 
            href="/business-opportunities-v1.0.html" 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="business-opportunities-link"
            className="flex items-center justify-between p-3 bg-[#1a0527] border border-[#4A0A66] rounded-lg hover:border-[#0FFFE2] transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#0FFFE2] to-[#22C55E] rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#1a0527]" />
              </div>
              <div>
                <p className="text-white font-medium group-hover:text-[#0FFFE2] transition-colors">
                  Business Opportunities v1.0
                </p>
                <p className="text-xs text-[#FDF2F8]/50">
                  Partnership guide for B2B, influencers & agencies
                </p>
              </div>
            </div>
            <span className="text-[#FDF2F8]/50 group-hover:text-[#0FFFE2]">
              Open &rarr;
            </span>
          </a>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && analyticsData && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="investor-analytics-preview-modal">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-xl border border-[#4A0A66] w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b border-[#4A0A66] flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-xl font-bold text-white">Investor Analytics Preview</h3>
                <p className="text-xs text-[#FDF2F8]/50">
                  Exported: {new Date(analyticsData.export_date).toLocaleString()}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  size="sm"
                  data-testid="copy-investor-analytics-btn"
                  className="border-[#4A0A66] text-[#FDF2F8]/70 hover:bg-[#4A0A66]/30"
                >
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
                <Button
                  onClick={downloadJSON}
                  variant="outline"
                  size="sm"
                  data-testid="preview-download-json-btn"
                  className="border-[#0FFFE2] text-[#0FFFE2] hover:bg-[#0FFFE2]/20"
                >
                  <Download className="w-3 h-3 mr-1" /> JSON
                </Button>
                <Button
                  onClick={downloadCSV}
                  variant="outline"
                  size="sm"
                  data-testid="preview-download-csv-btn"
                  className="border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/20"
                >
                  <Download className="w-3 h-3 mr-1" /> CSV
                </Button>
                <button 
                  onClick={() => setShowPreviewModal(false)} 
                  className="text-[#FDF2F8]/50 hover:text-white p-2"
                  data-testid="close-investor-preview-btn"
                  aria-label="Close investor analytics preview"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content - Scrollable */}
            <div className="p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                {/* User Metrics Section */}
                <div>
                  <SectionHeader title="User Metrics" icon={Users} color="#df1283" />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard 
                      title="Total Users" 
                      value={analyticsData.user_metrics?.total_users || 0}
                      icon={Users}
                      color="#df1283"
                    />
                    <StatCard 
                      title="Google OAuth" 
                      value={analyticsData.user_metrics?.by_auth_type?.google_oauth || 0}
                      color="#4285F4"
                    />
                    <StatCard 
                      title="Email/Password" 
                      value={analyticsData.user_metrics?.by_auth_type?.email_password || 0}
                      color="#FF7901"
                    />
                    <StatCard 
                      title="Onboarding Rate" 
                      value={analyticsData.user_metrics?.onboarding?.completion_rate || '0%'}
                      icon={UserCheck}
                      color="#0FFFE2"
                    />
                  </div>
                  
                  {/* Tier Breakdown */}
                  {analyticsData.user_metrics?.by_membership_tier && (
                    <div className="mt-3 p-3 bg-[#1a0527] border border-[#4A0A66] rounded-lg">
                      <p className="text-xs text-[#FDF2F8]/60 mb-2">Membership Tier Breakdown</p>
                      <div className="flex flex-wrap gap-3">
                        {Object.entries(analyticsData.user_metrics.by_membership_tier).map(([tier, count]) => (
                          <div key={tier} className="px-3 py-1 bg-[#4A0A66]/50 rounded-lg">
                            <span className="text-sm text-white capitalize">{tier}: </span>
                            <span className="text-sm font-bold text-[#0FFFE2]">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Active Users & Reactivation */}
                <div>
                  <SectionHeader title="Active Users & Retention" icon={Activity} color="#0FFFE2" />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard 
                      title="Active (7 Days)" 
                      value={analyticsData.active_users?.active_last_7_days || 0}
                      subValue={analyticsData.active_users?.activity_rate_7d}
                      color="#0FFFE2"
                    />
                    <StatCard 
                      title="Active (30 Days)" 
                      value={analyticsData.active_users?.active_last_30_days || 0}
                      subValue={analyticsData.active_users?.activity_rate_30d}
                      color="#0FFFE2"
                    />
                    <StatCard 
                      title="Reactivated Users" 
                      value={analyticsData.active_users?.reactivated_users || 0}
                      icon={Heart}
                      color="#22C55E"
                    />
                    <StatCard 
                      title="Recovered from Deletion" 
                      value={analyticsData.active_users?.recovered_from_deletion || 0}
                      icon={UserCheck}
                      color="#22C55E"
                    />
                  </div>
                </div>
                
                {/* Signup Trends */}
                <div>
                  <SectionHeader title="Signup Trends" icon={TrendingUp} color="#22C55E" />
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <StatCard 
                      title="Last 30 Days" 
                      value={analyticsData.signup_trends?.last_30_days || 0}
                      icon={TrendingUp}
                      color="#22C55E"
                    />
                    <StatCard 
                      title="Last 90 Days" 
                      value={analyticsData.signup_trends?.last_90_days || 0}
                      color="#22C55E"
                    />
                    <div className="bg-[#1a0527] border border-[#4A0A66] rounded-lg p-3">
                      <p className="text-xs text-[#FDF2F8]/60 mb-2">Monthly Breakdown</p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {analyticsData.signup_trends?.monthly_breakdown?.slice(0, 6).map((m) => (
                          <div key={m.month} className="flex justify-between text-xs">
                            <span className="text-[#FDF2F8]/70">{m.month}</span>
                            <span className="text-white font-medium">{m.signups}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Engagement */}
                <div>
                  <SectionHeader title="Engagement" icon={Zap} color="#FF7901" />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard 
                      title="Total Entries" 
                      value={analyticsData.engagement?.total_journal_entries || 0}
                      icon={FileText}
                      color="#FF7901"
                    />
                    <StatCard 
                      title="Entries Today" 
                      value={analyticsData.engagement?.entries_today || 0}
                      color="#22C55E"
                    />
                    <StatCard 
                      title="Entries This Week" 
                      value={analyticsData.engagement?.entries_this_week || 0}
                      color="#0FFFE2"
                    />
                    <StatCard 
                      title="Entries (30d)" 
                      value={analyticsData.engagement?.entries_last_30_days || 0}
                      color="#FF7901"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                    <StatCard 
                      title="Users with Entries" 
                      value={analyticsData.engagement?.users_with_entries || 0}
                      color="#FF7901"
                    />
                    <StatCard 
                      title="Activation Rate" 
                      value={analyticsData.engagement?.activation_rate || '0%'}
                      icon={Target}
                      color="#df1283"
                    />
                    <StatCard 
                      title="Total Logins" 
                      value={analyticsData.engagement?.total_logins || 0}
                      icon={Activity}
                      color="#8B5CF6"
                    />
                    <StatCard 
                      title="Avg Entries/Active User" 
                      value={analyticsData.engagement?.avg_entries_per_user || 0}
                      color="#EC4899"
                    />
                  </div>
                </div>
                
                {/* Feature Usage */}
                <div>
                  <SectionHeader title="Feature Adoption" icon={Zap} color="#8B5CF6" />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard 
                      title="Voice Typing Users" 
                      value={analyticsData.feature_usage?.voice_typing?.users || 0}
                      color="#8B5CF6"
                    />
                    <StatCard 
                      title="Transcriptions" 
                      value={analyticsData.feature_usage?.voice_typing?.total_transcriptions || 0}
                      color="#8B5CF6"
                    />
                    <StatCard 
                      title="AI Coach Users" 
                      value={analyticsData.feature_usage?.ai_coach?.users || 0}
                      color="#EC4899"
                    />
                    <StatCard 
                      title="AI Prompts" 
                      value={analyticsData.feature_usage?.ai_coach?.total_prompts || 0}
                      color="#EC4899"
                    />
                  </div>
                </div>
                
                {/* Gamification */}
                <div>
                  <SectionHeader title="Gamification" icon={Award} color="#F59E0B" />
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <StatCard 
                      title="Users with Streak" 
                      value={analyticsData.gamification?.users_with_active_streak || 0}
                      icon={Award}
                      color="#F59E0B"
                    />
                    <StatCard 
                      title="Highest Streak" 
                      value={analyticsData.gamification?.highest_streak || 0}
                      color="#F59E0B"
                    />
                    <StatCard 
                      title="Badges Earned" 
                      value={analyticsData.gamification?.total_badges_earned || 0}
                      color="#F59E0B"
                    />
                  </div>
                </div>
                
                {/* Banner Marketing */}
                <div>
                  <SectionHeader title="Banner Marketing ROI" icon={Megaphone} color="#06B6D4" />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard 
                      title="Total Banners" 
                      value={analyticsData.banner_marketing?.total_banners_created || 0}
                      subValue={`${analyticsData.banner_marketing?.active_banners || 0} active`}
                      color="#06B6D4"
                    />
                    <StatCard 
                      title="Impressions" 
                      value={analyticsData.banner_marketing?.total_impressions?.toLocaleString() || 0}
                      color="#06B6D4"
                    />
                    <StatCard 
                      title="Clicks" 
                      value={analyticsData.banner_marketing?.total_clicks?.toLocaleString() || 0}
                      subValue={analyticsData.banner_marketing?.overall_ctr}
                      color="#06B6D4"
                    />
                    <StatCard 
                      title="Conversions" 
                      value={analyticsData.banner_marketing?.total_conversions || 0}
                      color="#22C55E"
                    />
                  </div>
                  
                  {/* Top Banners */}
                  {analyticsData.banner_marketing?.top_performing_banners?.length > 0 && (
                    <div className="mt-3 p-3 bg-[#1a0527] border border-[#4A0A66] rounded-lg">
                      <p className="text-xs text-[#FDF2F8]/60 mb-2">Top Performing Banners</p>
                      <div className="space-y-2">
                        {analyticsData.banner_marketing.top_performing_banners.map((banner, i) => (
                          <div key={i} className="flex justify-between text-xs">
                            <span className="text-[#FDF2F8]/70 truncate max-w-[180px]">{banner.name}</span>
                            <div className="flex gap-2">
                              <span className="text-white">{banner.impressions?.toLocaleString()} imp</span>
                              <span className="text-[#06B6D4]">{banner.clicks} clicks</span>
                              <span className="text-[#22C55E]">{banner.conversions} goals</span>
                              <span className="text-[#0FFFE2]">{banner.conversion_rate || banner.ctr || '0%'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* B2B & Marketing */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <SectionHeader title="B2B" icon={Building2} color="#10B981" />
                    <div className="grid grid-cols-2 gap-3">
                      <StatCard 
                        title="B2B Clients" 
                        value={analyticsData.b2b?.total_clients || 0}
                        icon={Building2}
                        color="#10B981"
                      />
                      <StatCard 
                        title="B2B Users" 
                        value={analyticsData.b2b?.total_b2b_users || 0}
                        color="#10B981"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <SectionHeader title="Marketing" icon={Mail} color="#EF4444" />
                    <div className="grid grid-cols-2 gap-3">
                      <StatCard 
                        title="Campaigns" 
                        value={analyticsData.marketing?.email_campaigns_sent || 0}
                        icon={Mail}
                        color="#EF4444"
                      />
                      <StatCard 
                        title="Emails Sent" 
                        value={analyticsData.marketing?.total_emails_sent || 0}
                        color="#EF4444"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Referral Program */}
                <div>
                  <SectionHeader title="Referral Program" icon={Gift} color="#A855F7" />
                  <div className="grid grid-cols-2 gap-3">
                    <StatCard 
                      title="Total Referrals" 
                      value={analyticsData.marketing?.referral_program?.total_referrals || 0}
                      icon={Gift}
                      color="#A855F7"
                    />
                    <StatCard 
                      title="Successful Conversions" 
                      value={analyticsData.marketing?.referral_program?.successful_conversions || 0}
                      icon={CheckCircle}
                      color="#22C55E"
                    />
                  </div>
                </div>
                
                {/* Data Integrity */}
                <div className="p-4 bg-[#1a0527]/50 border border-[#4A0A66] rounded-lg">
                  <p className="text-xs text-[#FDF2F8]/60 mb-2">Data Integrity</p>
                  <div className="flex gap-6">
                    <div>
                      <span className="text-xs text-[#FDF2F8]/50">Active Accounts: </span>
                      <span className="text-sm text-white font-medium">
                        {analyticsData.data_integrity?.active_accounts || 0}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-[#FDF2F8]/50">Merged (Excluded): </span>
                      <span className="text-sm text-[#FF7901] font-medium">
                        {analyticsData.data_integrity?.merged_accounts || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorToolsTab;
